import React, { useCallback, useEffect, useState } from 'react';
import APIService from '../../services/api';

/**
 * Paramétrage du module LinkedIn — dans SA page du Marketplace (12.10 :
 * « la conf dans son module », pas dans Configuration).
 *
 * Ce que l'administrateur règle et lit ici :
 *  - si le serveur est relié à LinkedIn (sinon la raison, telle que servie) ;
 *  - OÙ partent les annonces : le fil de la personne qui publie, et la page
 *    entreprise quand LinkedIn l'aura ouverte (état servi, jamais deviné) ;
 *  - SON compte (connecter, reconnecter, délier) — chaque recruteur relie le
 *    sien depuis « Publier sur LinkedIn » sur un besoin ;
 *  - qui, dans l'organisation, a relié son compte, et jusqu'à quand ;
 *  - l'ADRESSE qui reçoit les candidatures des offres (13.10 : « l'adresse
 *    carrières, dans le module sur le Marketplace — l'adresse par défaut, que
 *    le client peut mettre ») : vide, c'est la boîte candidatures. Le serveur
 *    dit si elle arrive bien dans ERPIA (`note`) — l'écran ne le devine pas.
 *
 * Routes : GET /linkedin/connexions (admin) · GET /linkedin/etat ·
 * GET /linkedin/connexion?retour=/marketplace · DELETE /linkedin/connexion ·
 * GET / PUT /linkedin/reglages (admin).
 */
const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, className }) => <i className={`bi bi-${icon}${className ? ` ${className}` : ''}`} aria-hidden="true" />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const dateFr = (iso) => { const d = iso ? new Date(iso) : null; return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'; };

async function lire(url) {
  const r = await fetch(url, { headers: authH() });
  if (r.status === 404 || r.status === 501) return { absent: true };
  if (r.status === 403) return { interdit: true };
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.success === false) return { error: j.error || `Le serveur a répondu ${r.status}.` };
  return j;
}

export default function LinkedInModule({ showNotification }) {
  const [moi, setMoi] = useState(null);
  const [org, setOrg] = useState(null);
  const [absent, setAbsent] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [occupe, setOccupe] = useState(null);
  const [reglage, setReglage] = useState(null);
  const [adresse, setAdresse] = useState('');
  const [erreurAdresse, setErreurAdresse] = useState(null);

  const charger = useCallback(async () => {
    const [e, c] = await Promise.all([lire(`${BASE}/linkedin/etat`), lire(`${BASE}/linkedin/connexions`)]);
    if (e.absent || c.absent) { setAbsent(true); return; }
    if (e.error) setErreur(e.error); else setMoi(e);
    if (c.error) setErreur(c.error); else if (!c.interdit) setOrg(c);
    // L'adresse des candidatures : réservée aux administrateurs (403 → la
    // section ne s'affiche pas) ; route pas encore déployée → idem.
    const g = await lire(`${BASE}/linkedin/reglages`).catch(() => ({ absent: true }));
    if (!g.absent && !g.interdit && !g.error) { setReglage(g); setAdresse(g.adresse_candidatures || ''); }
  }, []);
  useEffect(() => { charger().catch(() => setErreur('Le serveur ne répond pas — réessayez dans un instant.')); }, [charger]);

  const connecter = async () => {
    setOccupe('connexion'); setErreur(null);
    try {
      const j = await lire(`${BASE}/linkedin/connexion?retour=${encodeURIComponent('/marketplace')}`);
      if (j.error || !j.url) { setErreur(j.error || 'Le serveur n\'a pas donné l\'adresse de connexion.'); return; }
      window.location.assign(j.url);
    } catch { setErreur('Le serveur ne répond pas — réessayez dans un instant.'); }
    finally { setOccupe(null); }
  };
  const delier = async () => {
    setOccupe('delier'); setErreur(null);
    try {
      const r = await fetch(`${BASE}/linkedin/connexion`, { method: 'DELETE', headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { setErreur(j.error || `Le serveur a répondu ${r.status}.`); return; }
      showNotification?.('Compte LinkedIn délié', 'success');
      await charger();
    } catch { setErreur('Le serveur ne répond pas — réessayez dans un instant.'); }
    finally { setOccupe(null); }
  };

  const enregistrerAdresse = async () => {
    setOccupe('adresse'); setErreurAdresse(null);
    try {
      const r = await fetch(`${BASE}/linkedin/reglages`, { method: 'PUT', headers: { ...authH(), 'Content-Type': 'application/json' }, body: JSON.stringify({ adresse_candidatures: adresse.trim() }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { setErreurAdresse(j.error || `Le serveur a répondu ${r.status}.`); return; }
      setReglage(j); setAdresse(j.adresse_candidatures || '');
      showNotification?.(j.message || 'Adresse enregistrée', 'success');
    } catch { setErreurAdresse('Le serveur ne répond pas — réessayez dans un instant.'); }
    finally { setOccupe(null); }
  };

  if (absent) {
    return <div className="li-mod erp-card"><p className="li-mod__muet">Le module LinkedIn arrive côté serveur : rien n'est encore branché.</p></div>;
  }
  const pret = moi ? moi.disponible !== false : true;
  const pages = org?.pages || moi?.pages || null;
  const comptes = org?.connexions || [];

  return (
    <div className="li-mod">
      {erreur && <p className="li-mod__erreur" role="alert">{erreur}</p>}

      <section className="li-mod__bloc erp-card">
        <h3 className="li-mod__titre">Où partent les annonces</h3>
        {!moi && !erreur && <p className="li-mod__muet"><BI icon="arrow-repeat" className="li-pub__tourne" /> Chargement…</p>}
        {moi && !pret && <p className="li-mod__texte">{moi.raison}</p>}
        {moi && pret && (
          <ul className="li-mod__liste">
            <li><BI icon="check-circle-fill" className="li-mod__ok" /> Sur le fil LinkedIn de la personne qui publie, avec le lien pour postuler sur ERPIA.</li>
            <li>
              <BI icon={pages?.ouverte ? 'check-circle-fill' : 'hourglass-split'} className={pages?.ouverte ? 'li-mod__ok' : 'li-mod__attente'} />
              {' '}{pages?.ouverte ? 'Au nom de la page entreprise : ouvert.' : (pages?.raison || 'Au nom de la page entreprise : pas encore ouvert.')}
            </li>
            <li><BI icon="clipboard" className="li-mod__neutre" /> Une offre de l'onglet Emplois se crée dans LinkedIn, avec le texte et le lien qu'ERPIA prépare (« Copier le texte de l'offre »).</li>
          </ul>
        )}
      </section>

      {reglage && (
        <section className="li-mod__bloc erp-card">
          <h3 className="li-mod__titre">Adresse des candidatures</h3>
          <p className="li-mod__texte">L'adresse à laquelle LinkedIn envoie chaque candidature, CV joint, pour les offres publiées depuis ERPIA. Laissez vide pour utiliser la boîte candidatures{reglage.boite_candidatures ? <> (<span className="li-mod__mono">{reglage.boite_candidatures}</span>)</> : null}.</p>
          <form className="li-mod__adresse" onSubmit={e => { e.preventDefault(); enregistrerAdresse(); }}>
            <label className="li-mod__champ">
              <span className="li-mod__sr">Adresse e-mail des candidatures</span>
              <input className="erp-field" type="email" value={adresse} onChange={e => setAdresse(e.target.value)}
                placeholder={reglage.boite_candidatures || 'carrieres@entreprise.ch'} aria-invalid={!!erreurAdresse} />
            </label>
            <button type="submit" className="erp-btn erp-btn--primary erp-btn--sm" disabled={!!occupe || adresse.trim() === (reglage.adresse_candidatures || '')}>
              <BI icon={occupe === 'adresse' ? 'arrow-repeat' : 'check2'} className={occupe === 'adresse' ? 'li-pub__tourne' : ''} /> Enregistrer l'adresse
            </button>
          </form>
          {erreurAdresse && <p className="li-mod__erreur" role="alert">{erreurAdresse}</p>}
          {reglage.adresse_effective && <p className="li-mod__texte">Les candidatures vont à <strong className="li-mod__mono">{reglage.adresse_effective}</strong>.</p>}
          {reglage.note && <p className="li-mod__alerte">{reglage.note}</p>}
        </section>
      )}

      <section className="li-mod__bloc erp-card">
        <h3 className="li-mod__titre">Mon compte</h3>
        {moi && pret && moi.connecte && (
          <>
            <p className="li-mod__texte">Relié : <strong>{moi.nom || 'votre compte'}</strong>, jusqu'au <span className="li-mod__mono">{dateFr(moi.expire_le)}</span>.</p>
            {moi.message && <p className="li-mod__alerte">{moi.message}</p>}
            <div className="li-mod__actions">
              <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={connecter} disabled={!!occupe}><BI icon="arrow-clockwise" /> Reconnecter</button>
              <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={delier} disabled={!!occupe}><BI icon="x-circle" /> Délier ce compte</button>
            </div>
          </>
        )}
        {moi && pret && !moi.connecte && (
          <>
            <p className="li-mod__texte">{moi.expiree ? moi.message : 'Aucun compte relié. Chaque recruteur relie le sien depuis « Publier sur LinkedIn » sur un besoin ; vous pouvez relier le vôtre ici.'}</p>
            <div className="li-mod__actions">
              <button type="button" className="erp-btn erp-btn--primary" onClick={connecter} disabled={!!occupe}>
                <BI icon={occupe === 'connexion' ? 'arrow-repeat' : 'linkedin'} className={occupe === 'connexion' ? 'li-pub__tourne' : ''} /> Connecter mon compte LinkedIn
              </button>
            </div>
          </>
        )}
      </section>

      <section className="li-mod__bloc erp-card">
        <h3 className="li-mod__titre">Comptes reliés dans l'organisation</h3>
        {org && comptes.length === 0 && <p className="li-mod__muet">Personne n'a encore relié son compte. La première publication d'un recruteur le lui proposera.</p>}
        {comptes.length > 0 && (
          <ul className="li-mod__comptes">
            {comptes.map((c, i) => (
              <li key={i} className={c.expiree ? 'is-expiree' : c.a_renouveler ? 'is-bientot' : ''}>
                <span className="li-mod__qui">{c.utilisateur || '—'}{c.nom_linkedin && c.nom_linkedin !== c.utilisateur ? <span className="li-mod__sous"> · LinkedIn : {c.nom_linkedin}</span> : null}</span>
                <span className="li-mod__mono">{c.expiree ? `expirée le ${dateFr(c.expire_le)}` : `jusqu'au ${dateFr(c.expire_le)}`}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
